import type { WistiaPlayer } from "@wistia/wistia-player"

import analyticsResource from "../analytics/analytics-resource"
import analyticsWistia from "../analytics/analytics-wistia"

type WistiaPlayerProps = {
  wistiaId: string
  resource?: { title: string; type: string; gated: string }
}

// Start fetching the player a little before it scrolls into view so autoplay
// videos begin on time rather than only once fully visible.
const ROOT_MARGIN = "400px 0px"

// Run non-critical work when the main thread is idle so booting the Wistia
// player (and its HLS engine) doesn't compete with the initial render / LCP.
// Falls back to a macrotask where requestIdleCallback is unavailable (Safari).
function whenIdle(callback: () => void): void {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(callback, { timeout: 2000 })
  } else {
    window.setTimeout(callback, 0)
  }
}

/**
 * Defo view for `[data-ca-wistia-player]`.
 *
 * Every embed is gated on visibility through a single IntersectionObserver
 * code path: above-the-fold embeds (e.g. a hero) intersect immediately, while
 * below-the-fold embeds only initialise as they approach the viewport. This
 * avoids booting every player on the page at once on initial load.
 */
export default function WistiaEmbed(
  el: HTMLElement,
  props: WistiaPlayerProps
): void {
  if (!("IntersectionObserver" in window)) {
    // No IntersectionObserver support: fall back to loading immediately.
    whenIdle(() => mountPlayer(el, props).catch(() => {}))
    return
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue
        obs.disconnect()
        whenIdle(() => mountPlayer(el, props).catch(() => {}))
      }
    },
    { rootMargin: ROOT_MARGIN }
  )

  observer.observe(el)
}

/**
 * Create and mount the Wistia player. The player web component (and its HLS
 * engine) is dynamically imported here so the heavy dependency is only
 * downloaded when a player is actually about to render, rather than eagerly on
 * initial page load.
 */
async function mountPlayer(
  el: HTMLElement,
  { wistiaId, resource }: WistiaPlayerProps
): Promise<void> {
  await import("@wistia/wistia-player")

  const player = document.createElement("wistia-player") as WistiaPlayer

  // Set player attributes
  player.setAttribute("player-color", "#171A1B")
  player.setAttribute("rounded-player", "24")

  player.mediaId = wistiaId
  player.swatch = false

  player.addEventListener("loaded-data", () => {
    el.dataset.mediaLoaded = "true"
  })

  analyticsWistia(player)

  if (resource) {
    player.dataset.gated = resource.gated
    player.dataset.resourceType = resource.type
    player.dataset.title = resource.title
    analyticsResource(player)
  }

  el.appendChild(player)
}
